import React, { useState } from "react";
import { BookOpen, Sparkles, Plus, Trash2, ShieldCheck, AlertCircle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WEEKLY_REFLECTION_PROMPTS, WEEKLY_REFLECTION_MAX, MAX_WEEKLY_REFLECTIONS } from "@/lib/dreamBridge";
interface WeeklyReflectionProps {
  reflections: {
    weekLabel: string;
    text: string;
  }[];
  isConsented: boolean;
  onAddReflection: (text: string, weekLabel?: string) => void;
  onRemoveReflection: (index: number) => void;
}
export default function WeeklyReflection({
  reflections,
  isConsented,
  onAddReflection,
  onRemoveReflection
}: WeeklyReflectionProps) {
  const [reflectionText, setReflectionText] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const currentCount = reflections.length;
  const isMaxReached = currentCount >= MAX_WEEKLY_REFLECTIONS;
  const handleSave = () => {
    const trimmed = reflectionText.trim();
    if (!trimmed) {
      setValidationError("Please enter a reflection before saving.");
      return;
    }
    if (trimmed.length > WEEKLY_REFLECTION_MAX) {
      setValidationError(`Reflection exceeds the maximum of ${WEEKLY_REFLECTION_MAX} characters.`);
      return;
    }
    const nextWeekLabel = `Week ${currentCount + 1}`;
    onAddReflection(trimmed, nextWeekLabel);
    setReflectionText("");
    setSelectedPrompt(null);
    setValidationError(null);
  };
  const handlePickPrompt = (prompt: string) => {
    setSelectedPrompt(prompt);
    // If textarea is empty, give user a gentle head start
    if (!reflectionText.trim()) {
      setReflectionText(`${prompt}\n`);
    }
  };
  return <div className="ql-glass p-5 sm:p-7 rounded-2xl border border-primary/30 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-border/60 pb-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <BookOpen className="size-4 text-primary shrink-0" />
            <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
              Weekly Reflection Journal
            </h4>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Pause once a week to notice how your practical moves felt, what worked, and what needs adjusting.
          </p>
        </div>

        <div className="self-start sm:self-auto font-mono text-xs text-primary px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 shrink-0">
          {currentCount} / {MAX_WEEKLY_REFLECTIONS} saved
        </div>
      </div>

      {/* Prompts list */}
      <div className="space-y-2">
        <span className="text-xs font-mono font-semibold text-foreground flex items-center gap-1.5">
          <Sparkles className="size-3 text-primary shrink-0" />
          <span>Thoughtful prompts to spark this week's reflection:</span>
        </span>
        <div className="grid grid-cols-1 gap-2 pt-1">
          {WEEKLY_REFLECTION_PROMPTS.map((prompt, idx) => {
          const isPicked = selectedPrompt === prompt;
          return <button key={idx} type="button" onClick={() => handlePickPrompt(prompt)} className={`text-left p-2.5 sm:p-3 rounded-xl border text-xs leading-relaxed transition-all cursor-pointer ${isPicked ? "bg-primary/10 border-primary text-foreground shadow-[0_0_10px_var(--glow-primary)]" : "bg-card/50 border-border/70 text-muted-foreground hover:text-foreground hover:border-primary/40"}`}>
                <span className="font-mono text-primary mr-1.5 font-bold">#{idx + 1}</span>
                <span>{prompt}</span>
              </button>;
        })}
        </div>
      </div>

      {/* New reflection entry form */}
      {!isMaxReached ? <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="weekly-reflection-input" className="text-xs font-mono font-semibold text-foreground">
              Write this week's notes:
            </Label>
            <span className="text-[11px] font-mono text-muted-foreground">
              {reflectionText.length} / {WEEKLY_REFLECTION_MAX}
            </span>
          </div>

          <Textarea id="weekly-reflection-input" value={reflectionText} onChange={e => {
        setReflectionText(e.target.value);
        if (validationError) setValidationError(null);
      }} maxLength={WEEKLY_REFLECTION_MAX} rows={4} placeholder="Record what happened this week, how you felt taking real action, and one gentle adjustment for next week..." className="ql-input min-h-[90px] text-base sm:text-sm text-foreground placeholder:text-muted-foreground" />

          {validationError && <p className="text-xs font-mono text-destructive flex items-center gap-1.5">
              <AlertCircle className="size-3.5 shrink-0" />
              <span>{validationError}</span>
            </p>}

          <div className="flex flex-col min-[480px]:flex-row items-stretch min-[480px]:items-center justify-between gap-3 pt-1">
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-primary shrink-0" />
              <span>
                {isConsented ? "Saved locally in your browser storage (private to your device)." : "Kept in memory only during this session unless browser saving is switched on."}
              </span>
            </div>

            <button type="button" onClick={handleSave} className="ql-btn min-h-[44px] text-xs font-serif shrink-0 justify-center">
              <Plus className="size-3.5 mr-1.5 shrink-0" />
              <span>Save this week's reflection</span>
            </button>
          </div>
        </div> : <div className="p-3.5 rounded-xl border border-border bg-card/60 text-xs text-muted-foreground text-center">
          You have reached the maximum of {MAX_WEEKLY_REFLECTIONS} weekly reflection entries.
        </div>}

      {/* List of past reflections */}
      {reflections.length > 0 && <div className="space-y-3 pt-3 border-t border-border/60">
          <h5 className="font-mono text-xs uppercase tracking-wider text-muted-foreground font-semibold">
            Saved Reflection Entries ({reflections.length})
          </h5>

          <div className="space-y-2.5">
            {reflections.map((entry, index) => <div key={index} className="p-3.5 sm:p-4 rounded-xl border border-border bg-card/70 flex items-start justify-between gap-3">
                <div className="space-y-1 min-w-0 flex-1">
                  <span className="font-mono text-xs font-bold text-primary">
                    {entry.weekLabel}
                  </span>
                  <p className="text-xs sm:text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed break-words">
                    {entry.text}
                  </p>
                </div>

                <button type="button" onClick={() => onRemoveReflection(index)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors rounded-lg cursor-pointer shrink-0" aria-label={`Remove reflection for ${entry.weekLabel}`} title="Remove this reflection">
                  <Trash2 className="size-4 shrink-0" />
                </button>
              </div>)}
          </div>
        </div>}
    </div>;
}